(ns io.daskul.tutorial.webapp.components.guardian-panel-logic
  "View-model for the student's Guardian tab (#974).

   The tab answers three questions about one student: who pays, who else is
   linked, and which discounts reach them. All of it is derived from three
   reads — the student's `student-guardian` rows, the payer's rows (for the
   siblings), and the payer's discounts — so nothing here needs a query per
   sibling.

   Pure: no React, no re-frame."
  (:require
   [clojure.string :as str]))

(defn- person-name [record path]
  (get-in record (conj path :person :first-name)))

(defn split-guardians
  "Split a student's guardian links into the one who pays and the rest.

   The payer is the row flagged `:primary-payer` — never the first row, and
   never `:primary-contact`, which is who you call rather than who you bill.
   Two flagged rows is a data bug; the earliest link wins so the page shows the
   same person on every reload."
  [links]
  (let [payers (->> links
                    (filter :primary-payer)
                    (sort-by :created-at))
        payer (first payers)]
    {:payer payer
     :others (vec (remove #(= % payer) links))}))

(defn discounts-for
  "The payer's discounts that land on one student: their own, plus every
   discount the payer holds for no student in particular."
  [discounts student-id]
  (vec (filter (fn [{:keys [beneficiary-id]}]
                 (or (nil? beneficiary-id) (= beneficiary-id student-id)))
               discounts)))

(defn siblings
  "The payer's other students, each with how many discounts already reach them.

   The count is a group-by over the payer's own discount payload — the same one
   the discounts table renders — so a family of six costs no more reads than a
   family of one."
  [payer-links discounts student-id]
  (->> payer-links
       (remove #(= student-id (:student-id %)))
       (mapv (fn [{:keys [student-id] :as link}]
               {:student-id student-id
                :first-name (person-name link [:student])
                :relationship (:relationship link)
                :discount-count (count (discounts-for discounts student-id))}))))

(defn applies-to-label
  "How far a discount reaches, in words — deleting a universal row takes it from
   every sibling at once, so the row has to say which kind it is."
  [{:keys [beneficiary-id]} {:keys [payer-name student-name]}]
  (if (nil? beneficiary-id)
    (str "All of " payer-name "'s students")
    (str student-name " only")))

(defn guardian-name
  "Full name for a guardian link, for the payer card and the other-guardians list."
  [link]
  (->> [(get-in link [:guardian :person :first-name])
        (get-in link [:guardian :person :last-name])]
       (remove str/blank?)
       (str/join " ")))
